import React, { useState, forwardRef, useEffect } from "react";
import { FormFieldLabel as Label, FormErrorLabel as ErrorLabel, FormFieldLabel } from "../../components/label/Labels";
import { useField } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import IMAGES from "../../assets/images/images";
import styles from "./dateTimePicker.module.css"
import Asterisk from "../asterisk/Asterisk";
import { Popover, ArrowContainer } from 'react-tiny-popover'

const CustomInput = forwardRef(
    ({ value, onClick, placeholder }, ref) => {
        return (
            <div className={styles.dateInputWrapper} onClick={onClick}>
                <input
                    ref={ref}
                    value={value || ""}
                    placeholder={placeholder}
                    className={styles.dateInput}
                    readOnly
                />
                <img className={styles.calendarIcon} src={IMAGES.timeicon} alt="calendar" />
            </div>
        );
    }
);

const Tooltip = ({ text }) => {
    let [isPopoverOpen, setIsPopoverOpen] = useState(false);
    return (
        <Popover
            isOpen={isPopoverOpen}
            positions={['top']}
            containerClassName={styles.popoverContainer}
            content={({ position, childRect, popoverRect }) => (
                <ArrowContainer
                    position={position}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor={'#f5f3f3'}
                    arrowSize={10}
                >
                    <div className={styles.tooltipBody}>
                        {text}
                    </div>
                </ArrowContainer>
            )}
        >
            <img
                src={IMAGES.infoCircle}
                alt="info"
                onMouseEnter={() => setIsPopoverOpen(true)}
                onMouseLeave={() => setIsPopoverOpen(false)}
            />
        </Popover>
    );
};

const defaultDatePickerProps = {
    showTimeSelect: true,
    timeIntervals: 5,
    timeFormat: "HH:mm",
    dateFormat: "dd/MM/yyyy HH:mm",
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: "select",
    showPopperArrow: false,
    popperPlacement: "bottom-start",
};

export const DateTimePicker = ({
    disabled = false,
    isPreviewMode,
    isExpandedField,
    onCalendarOpen,
    ...props
}) => {

    const [field, meta, helpers] = useField(props);
    const hasError = meta.touched && meta.error ? true : false;
    const inputClass = hasError ? "input error" : "input";
    const selectedDate = field.value ? moment(field.value).isValid() ? moment(field.value).toDate() : null : null;
    const now = new Date();
    const scrollTime = field.value ? selectedDate : new Date(0, 0, 0, 0, 0);
    const [openDropdown, setOpenDropdown] = useState(null);

    useEffect(() => {
        const handleClick = () => setOpenDropdown(null);
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, []);

    const renderCustomHeader = ({ date, changeYear, changeMonth }) => {
    const current = props.isUTCMode ? moment().utc() : moment();
    const selectedYear = moment(date).year();
    const selectedMonth = moment(date).month();
    const currentYear = current.year();
    const currentMonth = current.month();
    const months = moment.months();

    const yearOptions = Array.from({ length: 10 }, (_, i) => ({
        value: currentYear - i,
        label: currentYear - i,
    }));

    const monthOptions = months.map((m, index) => ({
        value: index,
        label: m,
        disabled: selectedYear === currentYear && index > currentMonth,
    }));

    const CustomDropdown = ({ type, value, options, onChange }) => {
        const isOpen = openDropdown === type;
        return (
            <div className={styles.dropdownWrapper}>
                
                <div
                    className={styles.dropdownSelected}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(isOpen ? null : type);
                    }}
                >
                    {type === "month" ? options.find((opt) => opt.value === value)?.label : value}
                    <img className={styles.dropdownicon} src={IMAGES.arrowdown} alt="dropdownicon" />
                </div>

                {isOpen && (
                    <div className={styles.dropdownMenu}>
                        {options.map((opt) => {
                            const isSelected = opt.value === value;
                            return(
                                <div
                                    key={opt.value}
                                    className={styles.dropdownItem}
                                    style={{
                                        opacity: opt.disabled ? 0.4 : 1,
                                        cursor: opt.disabled ? "not-allowed" : "pointer",
                                        backgroundColor: isSelected ? "#216ba5" : "transparent",
                                        fontWeight: isSelected ? "700" : "400",
                                        color: isSelected ? "#FFFFFF" : "",
                                    }}
                                    onClick={() => {
                                        if (opt.disabled) return;
                                        onChange(opt.value);
                                        setOpenDropdown(null);
                                    }}
                                >
                                    {opt.label}
                                </div>
                        )})}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.customHeader}>
            <CustomDropdown
                type="year"
                value={selectedYear}
                options={yearOptions}
                onChange={(val) => changeYear(val)}
            />

            <CustomDropdown
                type="month"
                value={selectedMonth}
                options={monthOptions}
                onChange={(val) => changeMonth(val)}
            />
        </div>
    );
};

    return (
        <div className={props.isExpandedField ? `${styles.Field} ${styles.expandedField}` : `${styles.Field}`}>
            <div>
                <Label text={props.label} style={styles.lable} />
                {props.isMandatory && <Asterisk />}
                {props.showTooltip && !isPreviewMode && (
                    <>
                        &nbsp;&nbsp;
                        <Tooltip text={props.tooltipText} />
                    </>
                )}
            </div>
            {!isPreviewMode ? (
                <>
                    <DatePicker
                        popperClassName={styles.myDatepicker}
                        selected={selectedDate}
                        onChange={(date) => {
                            helpers.setTouched(true);
                            if (!date) {
                                helpers.setValue("");
                                return;
                            }
                            helpers.setValue(
                                moment(date).format("YYYY-MM-DDTHH:mm")
                            );
                        }}
                        onCalendarClose={() => helpers.setTouched(true)}
                        onBlur={() => helpers.setTouched(true)}
                        onCalendarOpen={() => {
                            if (onCalendarOpen) {
                                if (!field.value) {
                                    setTimeout(() => {
                                        const timeList = document.querySelector(
                                            ".react-datepicker__time-list"
                                        );
                                        if (timeList) {
                                            timeList.scrollTop = 0;
                                        }
                                    }, 0);
                                }
                                onCalendarOpen();
                            }
                        }}
                        minTime={props.datePickerProps?.minTime}
                        maxTime={props.datePickerProps?.maxTime}
                        maxDate={props.datePickerProps?.maxDate}
                        disabled={disabled}
                        placeholderText={props.placeholder || "dd/mm/yyyy, HH:mm"}
                        scrollToTime={scrollTime}
                        customInput={<CustomInput />}
                        renderCustomHeader={renderCustomHeader}
                        shouldCloseOnSelect={false}
                        {...defaultDatePickerProps}
                        {...props.datePickerProps}
                    />
                    <span className={styles.textLimitInfo}>{props.textLimit}</span>
                    {hasError ? <ErrorLabel text={meta.error} /> : null}
                </>
            ) : (
                <div className="previewText">
                    {field.value
                        ? moment(field.value).format("DD MMM YYYY HH:mm")
                        : "---"}
                </div>
            )}
        </div>
    );
};